import {
  Button,
  Col,
  Form,
  message,
  Modal,
  Radio,
  Result,
  Space,
  Steps,
  theme,
  Typography,
} from "antd";
import { ReactNode, useEffect, useRef, useState, useCallback } from "react";
import { DynamicSchema } from "./DynamicForm.default";
import DynamicForm from "./DynamicForm";
import Loader from "./Loader";
import { InputObject } from "./default";
import { RegistrationProcessStatus } from "../repository/auth.default";

export type WizardStep = {
  title: string;
  content?: ReactNode;
  schema?: DynamicSchema;
  createSchema?: (values: any) => DynamicSchema;
  validate?: (values: any) => boolean;
};

export interface DynamicFormWizardActionsProps<T extends Record<string, any>> {
  // Core wizard properties
  readonly title: string;
  readonly open?: boolean;
  readonly steps: WizardStep[];
  readonly initialValues?: T;
  readonly onFinish?: () => void;
  readonly onCancel?: () => void;

  // Option selection (for wizards that support single/multiple modes)
  readonly showOptionSelection?: boolean;
  readonly optionSelectionText?: string;
  readonly optionLabels?: {
    single: string;
    multiple: string;
  };

  // File upload functionality
  readonly fileUploadSupport?: boolean;
  readonly fileUploadText?: string;
  readonly fileUploadButtonText?: string;
  readonly downloadTemplateSupport?: boolean;
  readonly downloadTemplateButtonText?: string;
  readonly fileUploadSizeLimit?: number; // in MB
  readonly fileUploadErrorText?: string;
  readonly fileUploadAccept?: string;

  // API calls
  readonly apiCalls?: {
    submit?: (values: T) => Promise<{
      status: RegistrationProcessStatus;
      textLog: string[];
    }>;
    submitFile?: (
      file: File,
      values: T,
    ) => Promise<{
      status: RegistrationProcessStatus;
      textLog: string[];
    }>;
    downloadTemplate?: () => Promise<void>;
    previewFile?: (file: File, values: T) => Promise<any>;
  };

  // UI configuration
  readonly confirmButtonText?: string;
  readonly cancelButtonText?: string;
  readonly backButtonText?: string;
  readonly nextButtonText?: string;
  readonly completeButtonText?: string;
  readonly retryButtonText?: string;
  readonly closeButtonText?: string;
  readonly resetButtonText?: string;
  readonly errorText?: string;

  // Request functions for dynamic data
  readonly requestFunctions?: {
    [key: string]: (search: string) => Promise<InputObject[]>;
  };
}

/**
 * A component that encapsulates wizard form logic and provides a clean API for multi-step form handling.
 */
function DynamicFormWizardActions<T extends Record<string, any>>(
  props: DynamicFormWizardActionsProps<T>,
) {
  const { token } = theme.useToken();
  const [modal, modalHolder] = Modal.useModal();
  const [form] = Form.useForm<T>();
  const values = Form.useWatch<T>([], form);
  const fileRef = useRef<HTMLInputElement>(null!);
  const [messageApi, contextHolder] = message.useMessage();

  // Wizard state
  const [step, setStep] = useState(0);
  const [stepOption, setStepOption] = useState<"single" | "multiple">("single");
  const [valuesValid, setValuesValid] = useState(false);
  const [wizardValues, setWizardValues] = useState<T>(
    (props.initialValues as T) || ({} as T),
  );
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);

  // Status state
  const [loading, setLoading] = useState(false);
  const [finish, setFinish] = useState(false);
  const [error, setError] = useState(false);
  const [status, setStatus] = useState<"success" | "warning" | "error">(
    "success",
  );
  const [title, setTitle] = useState("");
  const [logs, setLogs] = useState<string[]>([]);

  // Reset all state
  const reset = useCallback(() => {
    setStep(0);
    setStepOption("single");
    setValuesValid(false);
    setFinish(false);
    setError(false);
    setUploadedFile(null);
    setStatus("success");
    setLogs([]);
    setWizardValues((props.initialValues as T) || ({} as T));
    form.resetFields();
  }, [form, props.initialValues]);

  // Initialize form when modal opens
  useEffect(() => {
    if (props.open) {
      form.setFieldsValue((props.initialValues as any) || {});
    } else {
      reset();
    }
  }, [form, props.initialValues, props.open, reset]);

  // Update wizard values when form values change
  useEffect(() => {
    form
      .validateFields({
        validateOnly: true,
      })
      .then(() => {
        setValuesValid(true);
      })
      .catch(() => {
        setValuesValid(false);
      });

    // Update wizard values with current form values
    if (values) {
      setWizardValues((prevValues) => ({ ...prevValues, ...values }));
    }
  }, [form, values, props.open]);

  // Update form with wizard values when step changes
  useEffect(() => {
    if (wizardValues && Object.keys(wizardValues).length > 0) {
      form.setFieldsValue(wizardValues);
    }
  }, [form, step, wizardValues]);

  // Step navigation functions
  function onStepBack(): void {
    setStep(step - 1);
  }

  function onStepNext(): void {
    // Save current form values to wizard values before moving to next step
    const currentValues = form.getFieldsValue();
    setWizardValues((prevValues) => ({ ...prevValues, ...currentValues }));
    setStep(step + 1);
  }

  function onStepFinish(): void {
    setLoading(true);
    setError(false);

    if (stepOption === "single") {
      // Submit form data
      if (props.apiCalls?.submit) {
        props.apiCalls
          .submit(wizardValues)
          .then((result) => {
            handleSubmitResult(result);
          })
          .catch(() => {
            handleSubmitError();
          });
      }
    } else if (uploadedFile && props.apiCalls?.submitFile) {
      // Submit file data
      props.apiCalls
        .submitFile(uploadedFile, wizardValues)
        .then((result) => {
          handleSubmitResult(result);
        })
        .catch(() => {
          handleSubmitError();
        });
    }
  }

  // Handle API response
  function handleSubmitResult(result: {
    status: RegistrationProcessStatus;
    textLog: string[];
  }): void {
    setFinish(true);
    setLoading(false);
    setLogs(result.textLog);

    switch (result.status) {
      case RegistrationProcessStatus.Success:
        setTitle("Operation completed successfully");
        setStatus("success");
        break;
      case RegistrationProcessStatus.Error:
        setTitle("Operation failed");
        setStatus("error");
        break;
      case RegistrationProcessStatus.Unknown:
        setTitle("Something went wrong, try again");
        setStatus("warning");
        break;
    }

    props.onFinish?.();
  }

  // Handle API error
  function handleSubmitError(): void {
    setFinish(false);
    setLoading(false);
    setError(true);
    setStatus("error");
    setTitle(props.errorText || "Something went wrong, try again");
  }

  // Option selection handler
  function onOptionChange(option: "single" | "multiple"): void {
    setStepOption(option);
  }

  // File upload handlers
  function onBrowseFile(): void {
    fileRef.current.click();
  }

  function onUploadFile(e: React.ChangeEvent): void {
    const file: File = (e.target as any).files[0];
    if (file) {
      const mb = file.size / 1024 / 1024;
      const limit = props.fileUploadSizeLimit || 100;

      if (mb > limit) {
        messageApi.open({
          type: "error",
          content: props.fileUploadErrorText || "File size limit exceeded",
        });
      } else {
        setUploadedFile(file);

        // If preview functionality is available, use it
        if (props.apiCalls?.previewFile) {
          setLoading(true);
          props.apiCalls
            .previewFile(file, wizardValues)
            .then((previewData) => {
              setLoading(false);
              form.setFieldsValue(previewData);
            })
            .catch(() => {
              setLoading(false);
              messageApi.error("Failed to preview file");
            });
        }
      }
    }
  }

  // Template download handler
  function onDownloadTemplate(): void {
    if (!props.apiCalls?.downloadTemplate) return;

    const loading = modal.info({
      content: (
        <Typography.Text className="m-1 ml-2 inline-block">
          Downloading template file..
        </Typography.Text>
      ),
      footer: null,
      centered: true,
      icon: <Loader />,
    });

    props.apiCalls
      .downloadTemplate()
      .then(() => {
        loading.destroy();
      })
      .catch(() => {
        loading.destroy();
        messageApi.error("Failed to download template");
      });
  }

  // Cancel handler
  function onCancel(): void {
    props.onCancel?.();
  }

  // Reset handler
  function onReset(): void {
    reset();
  }

  // Validate current step
  function isStepValid(): boolean {
    if (step === 0 && props.showOptionSelection) {
      return valuesValid;
    } else if (props.fileUploadSupport && stepOption === "multiple") {
      return uploadedFile != null;
    } else {
      // Use step-specific validation if provided
      const currentStep = props.steps[step];
      if (currentStep.validate) {
        return currentStep.validate(values);
      }
      return valuesValid;
    }
  }

  // Get current step schema
  function getCurrentStepSchema(): DynamicSchema | undefined {
    const currentStep = props.steps[step];
    if (currentStep.schema) {
      return currentStep.schema;
    } else if (currentStep.createSchema && wizardValues) {
      return currentStep.createSchema(wizardValues);
    }
    return undefined;
  }

  // Render wizard
  return (
    <>
      <Modal
        open={props.open}
        centered
        title={props.title}
        footer={null}
        maskClosable={false}
        onCancel={onCancel}
      >
        {/* Steps */}
        <Steps
          current={step}
          items={props.steps.map((s) => ({ title: s.title }))}
        />

        <Col className="flex max-h-[512px] min-w-[392px] max-w-[512px] flex-col overflow-hidden overflow-y-auto">
          {/* Content */}
          <Col className="flex max-h-[392px] min-h-[392px] flex-col overflow-hidden overflow-y-auto">
            {/* Step 0: Option Selection (if enabled) */}
            {step === 0 && props.showOptionSelection && (
              <Col className="flex flex-auto flex-col items-center justify-center pb-4 pt-4">
                <Typography.Paragraph>
                  {props.optionSelectionText || "Select an option:"}
                </Typography.Paragraph>

                {/* Form for first step */}
                <DynamicForm
                  className="w-96"
                  form={form}
                  schema={getCurrentStepSchema()}
                />

                {/* Option selection */}
                <Radio.Group
                  className="mt-4"
                  value={stepOption}
                  onChange={(e) => onOptionChange(e.target.value)}
                >
                  <Radio value={"single"}>
                    {props.optionLabels?.single || "Single"}
                  </Radio>
                  <Radio value={"multiple"}>
                    {props.optionLabels?.multiple || "Multiple"}
                  </Radio>
                </Radio.Group>
              </Col>
            )}

            {/* Custom step content or dynamic form */}
            {!props.showOptionSelection || step > 0 ? (
              <Col className="flex flex-auto flex-col pb-4 pt-4">
                {/* If step has custom content, render it */}
                {props.steps[step].content ? (
                  props.steps[step].content
                ) : (
                  <>
                    {/* For single mode or non-file steps, show the form */}
                    {(stepOption === "single" || !props.fileUploadSupport) && (
                      <DynamicForm
                        className="w-auto"
                        form={form}
                        schema={getCurrentStepSchema()}
                      />
                    )}

                    {/* For multiple mode with file upload, show file upload UI */}
                    {stepOption === "multiple" && props.fileUploadSupport && (
                      <Col className="flex flex-auto flex-col items-center justify-center">
                        <Typography.Paragraph>
                          {props.fileUploadText || "Upload a file"}
                        </Typography.Paragraph>

                        {uploadedFile && (
                          <Typography.Paragraph
                            style={{ color: token.colorPrimaryActive }}
                          >
                            Selected file: {uploadedFile?.name}
                          </Typography.Paragraph>
                        )}

                        <Col className="flex flex-row justify-center">
                          <Space className="flex flex-col">
                            <Button type="primary" onClick={onBrowseFile}>
                              {props.fileUploadButtonText || "Browse File"}
                            </Button>

                            {props.downloadTemplateSupport && (
                              <Button onClick={onDownloadTemplate}>
                                {props.downloadTemplateButtonText ||
                                  "Download Template"}
                              </Button>
                            )}
                          </Space>
                        </Col>

                        <input
                          className="hidden"
                          ref={fileRef}
                          hidden={true}
                          type="file"
                          accept={props.fileUploadAccept}
                          onChange={onUploadFile}
                        />
                      </Col>
                    )}
                  </>
                )}
              </Col>
            ) : null}

            {/* Final step - show result */}
            {step === props.steps.length - 1 && (
              <Col className="flex flex-auto flex-col items-center justify-center">
                {!loading && !finish && !error && (
                  <Typography.Paragraph>
                    {stepOption === "single"
                      ? "Complete the operation"
                      : "Complete the operation with file"}
                  </Typography.Paragraph>
                )}

                {loading && !finish && !error && (
                  <Typography.Paragraph>Processing...</Typography.Paragraph>
                )}

                {loading && <Loader />}

                {(finish || error) && (
                  <Result
                    status={status}
                    title={title}
                    extra={
                      <Typography.Paragraph className="text-start">
                        {logs.map((text, key) => (
                          <Typography.Text
                            className="inline-flex"
                            key={key}
                            type="secondary"
                          >
                            {text}
                          </Typography.Text>
                        ))}
                      </Typography.Paragraph>
                    }
                  />
                )}
              </Col>
            )}
          </Col>

          {/* Actions */}
          {!finish && (
            <Space className="sticky bottom-0 flex flex-row items-center justify-end p-2">
              <Button
                className="ml-2"
                disabled={
                  step < 1 || (step === props.steps.length - 1 && !error)
                }
                onClick={onStepBack}
              >
                {props.backButtonText || "Back"}
              </Button>

              {step < props.steps.length - 1 && (
                <Button
                  type="primary"
                  disabled={!isStepValid()}
                  onClick={onStepNext}
                >
                  {props.nextButtonText || "Next"}
                </Button>
              )}

              {step === props.steps.length - 1 && (
                <Button
                  type="primary"
                  disabled={!isStepValid()}
                  onClick={onStepFinish}
                >
                  {error
                    ? props.retryButtonText || "Retry"
                    : props.completeButtonText || "Complete"}
                </Button>
              )}
            </Space>
          )}

          {finish && (
            <Space className="sticky bottom-0 flex flex-row items-center justify-end p-2">
              <Button className="ml-2" onClick={onCancel}>
                {props.closeButtonText || "Close"}
              </Button>
              <Button type="primary" onClick={onReset}>
                {props.resetButtonText || "Reset"}
              </Button>
            </Space>
          )}
        </Col>
      </Modal>
      {contextHolder}
      {modalHolder}
    </>
  );
}

export default DynamicFormWizardActions;
